import React, { useState, useEffect } from 'react';
import { Loader } from './Components/Loader';
import { Navbar } from './Components/Navbar';
import { Home } from './Components/Home';
import { About2 } from './Components/About2';
import Skills from './Components/Skills';
import Tools from './Components/Tools';
import ProjectContainer from './Components/ProjectComp';
import { GitStats } from './Components/GitStats';
import Contact from './Components/Contact';
import { Footer } from './Components/Footer';
import { AdminDashboard, ProjectLog } from './Components/AdminDashboard';
import { AnimatedBackground } from './Components/AnimatedBackground';
import { ResumeModal } from './Components/ResumeModal';

// Default initial data for Alok Singh redone to match the new schema
const DEFAULT_PROJECTS: ProjectLog[] = [
  {
    id: 'fluxx_india',
    name: 'Fluxx India Website, CRM & Backend',
    badges: ['Node.js', 'Express.js', 'React', 'PostgreSQL', 'Role-Based Auth', 'Email Automation'],
    description: 'Developed the primary corporate website and engineered its integrated CRM + Backend. Built role-based authentication, Lead LMS and tracking systems, automated email notifications triggered on status basis, and custom frontend controls.',
    date: 'Oct 2025 - Present',
    published: true,
    featured: true,
    category: 'Full Stack',
    githubUrl: 'https://github.com/aloki9singh',
    liveUrl: 'https://www.fluxxindia.com/',
    order: 0
  },
  {
    id: 'fluxx_events',
    name: 'Fluxx Events Platform',
    badges: ['React', 'Tailwind CSS', 'Node.js', 'PostgreSQL'],
    description: 'Co-developed and structured the in-house event booking and management system for Fluxx Events. Engineered interactive components for event scheduling, user reservations, and layout management.',
    date: 'Dec 2025',
    published: true,
    featured: true,
    category: 'Frontend',
    githubUrl: 'https://github.com/aloki9singh',
    liveUrl: 'https://www.fluxxevents.com/',
    order: 1
  },
  {
    id: 'koncept_conference',
    name: 'Koncept Conference Portal',
    badges: ['React', 'Tailwind CSS', 'Express.js', 'MongoDB'],
    description: 'Engineered the frontend and custom interactive layout of the in-house Koncept Conference portal. Designed components for schedules, speaker lineups, and seamless client registrations.',
    date: 'Jan 2026',
    published: true,
    featured: false,
    category: 'Frontend',
    githubUrl: 'https://github.com/aloki9singh',
    liveUrl: 'https://www.konceptconference.com/',
    order: 2
  },
  {
    id: 'fluxx_groups_crm',
    name: 'Fluxx Groups Enterprise CRM',
    badges: ['React', 'Redux', 'Node.js', 'PostgreSQL', 'PM2'],
    description: 'Designed and deployed workflow management modules for the central enterprise CRM at Fluxx Groups. Handled secure logging, active query optimization, process monitor status tracking, and automated routing.',
    date: 'Feb 2026',
    published: true,
    featured: true,
    category: 'Full Stack',
    githubUrl: 'https://github.com/aloki9singh',
    liveUrl: 'https://crm.fluxxgroups.com/',
    order: 3
  },
  {
    id: 'futuristic_portfolio',
    name: 'Futuristic Developer Portfolio',
    badges: ['React', 'TypeScript', 'Tailwind CSS', 'Framer Motion'],
    description: 'A self-representing, interactive Awwwards-quality developer portfolio. Designed with premium glassmorphism panels, customized particle motion backgrounds, scroll transitions, and a secure local CMS admin interface for log management.',
    date: 'Jul 2026',
    published: true,
    featured: false,
    category: 'Frontend',
    githubUrl: 'https://github.com/aloki9singh/aloki9singh.github.io',
    liveUrl: 'https://aloki9singh.github.io',
    order: 4
  }
];

function App() {
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [isResumeOpen, setIsResumeOpen] = useState(false);
  const [projects, setProjects] = useState<ProjectLog[]>([]);

  // Load projects from localStorage or use defaults
  useEffect(() => {
    const saved = localStorage.getItem('alok_portfolio_logs_v2');
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as ProjectLog[];
        const merged = [...parsed];
        DEFAULT_PROJECTS.forEach((defProj) => {
          if (!merged.some((p) => p.id === defProj.id)) {
            merged.push(defProj);
          }
        });
        const sorted = merged.sort((a, b) => a.order - b.order);
        setProjects(sorted);
        localStorage.setItem('alok_portfolio_logs_v2', JSON.stringify(sorted));
      } catch (e) {
        setProjects(DEFAULT_PROJECTS);
      }
    } else {
      setProjects(DEFAULT_PROJECTS);
      localStorage.setItem('alok_portfolio_logs_v2', JSON.stringify(DEFAULT_PROJECTS));
    }
  }, []);

  // Listen to hash changes to support the /admin routing naturally
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#admin') {
        setShowAdmin(true);
      } else {
        setShowAdmin(false);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const saveProjects = (updatedList: ProjectLog[]) => {
    // Re-assign correct order values based on indices
    const ordered = updatedList.map((p, index) => ({ ...p, order: index }));
    setProjects(ordered);
    localStorage.setItem('alok_portfolio_logs_v2', JSON.stringify(ordered));
  };

  const handleAddProject = (newProj: Omit<ProjectLog, 'id' | 'order'>) => {
    const project: ProjectLog = {
      ...newProj,
      id: Date.now().toString(),
      order: 0 // Will sit at top
    };
    saveProjects([project, ...projects]);
  };

  const handleUpdateProject = (updatedProj: ProjectLog) => {
    const updated = projects.map((p) => (p.id === updatedProj.id ? updatedProj : p));
    saveProjects(updated);
  };

  const handleDeleteProject = (id: string) => {
    const filtered = projects.filter((p) => p.id !== id);
    saveProjects(filtered);
  };

  const handleReorder = (newOrderedList: ProjectLog[]) => {
    saveProjects(newOrderedList);
  };

  const closeAdmin = () => {
    window.location.hash = '';
    setShowAdmin(false);
  };

  const openAdmin = () => {
    window.location.hash = 'admin';
    setShowAdmin(true);
  };

  return (
    <div className="relative min-h-screen bg-cyber-black text-white selection:bg-cyber-cyan selection:text-black">
      {/* Premium animated background */}
      <AnimatedBackground />

      {loading && <Loader onComplete={() => setLoading(false)} />}

      {!loading && (
        <div className="relative z-10">
          <Navbar onOpenAdmin={openAdmin} onOpenResume={() => setIsResumeOpen(true)} />
          
          <Home onOpenResume={() => setIsResumeOpen(true)} />
          <About2 />
          <Skills />
          <Tools />
          
          {/* Display public-facing components, only passing published projects */}
          <ProjectContainer projects={projects.filter(p => p.published)} />
          
          <GitStats />
          
          <div className="relative z-20">
            <Contact />
            <Footer />
          </div>

          {showAdmin && (
            <AdminDashboard
              projects={projects}
              onAdd={handleAddProject}
              onUpdate={handleUpdateProject}
              onDelete={handleDeleteProject}
              onReorder={handleReorder}
              onClose={closeAdmin}
            />
          )}

          {isResumeOpen && (
            <ResumeModal
              isOpen={isResumeOpen}
              onClose={() => setIsResumeOpen(false)}
              projects={projects}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default App;
